// src/pages/Dashboard.js - leader home page "Přehled".
//
// Audit FL-33 / §5.1: instead of bare counters the page lists what needs to be done
// (register pre-registrations, upload music, confirm presence, pay invoices), shows
// upcoming competitions with their registration state and deadline, and has a compact
// group card with the logo. A failed load shows an error with retry, not zeros.
// Deadlines from the organiser's rules: online presence and payment a week before the
// competition (the nearest one is shown at the to-dos, amber/red in the last week); an
// invoice to-do for entries with confirmed presence; before the season a nudge to
// pre-register; a collapsible "Jak na registraci" with the four steps.
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Award,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  CreditCard,
  HelpCircle,
  Music2,
  Users,
  AlertTriangle,
  ListChecks,
  Receipt,
} from 'lucide-react';
import useFetch from '../hooks/useFetch';
import api, { choreographiesApi, competitionsApi, dancersApi } from '../services/api';
import {
  formatDate,
  formatDateLong,
  countLabel,
  daysUntil,
  isPastCompetition,
  isRegistrationClosed,
  presenceDeadline,
  showInCompetitionLists,
} from '../utils/format';
import { PageHeader, Spinner } from '../components/ui';
import LogoUploader from '../components/LogoUploader';
import LoadError from '../components/leader/LoadError';
import {
  countdownText,
  DeadlineText,
  RegistrationBadge,
  CompetitionTypeBadge,
} from '../components/leader/CompetitionBadges';

const CHOREO_FORMS = ['choreografie', 'choreografie', 'choreografií'];
const INVOICEABLE = ['confirmed', 'approved'];
const isInvoiceable = (item) => (typeof item.invoiceable === 'boolean' ? item.invoiceable : INVOICEABLE.includes(item.status));

/** The earliest presence (= payment) deadline of the competitions of `items` still ahead. */
function nearestDeadline(items) {
  const dates = items
    .map((item) => item.competition)
    .filter((competition) => competition && !isPastCompetition(competition))
    .map(presenceDeadline)
    .filter(Boolean)
    .sort();
  return dates[0] || null;
}

/** urgent (amber) in the last week before the deadline, critical (red) in its last two days or after it. */
function deadlineUrgency(date) {
  const days = daysUntil(date);
  if (days === null) return null;
  if (days <= 2) return 'critical';
  if (days <= 7) return 'urgent';
  return null;
}

function withDeadline(item, label, date) {
  if (!date) return item;
  const urgency = deadlineUrgency(date);
  return { ...item, deadline: { label, date }, urgent: item.urgent || Boolean(urgency), critical: urgency === 'critical' };
}

function buildTodo({ dancers, choreographies, competitions, invoices, pending }) {
  const todo = [];
  // Only competitions that are still ahead: old seasons must not nag (their statuses are often
  // left as they were, so the date decides).
  const ahead = choreographies.filter(
    (c) => !['finished', 'archived'].includes(c.competition?.status) && !isPastCompetition(c.competition)
  );
  const canRegister = ahead.filter((c) => c.canRegister && !isRegistrationClosed(c.competition));
  const drafts = ahead.filter((c) => c.status === 'draft');
  const withoutMusic = drafts.filter((c) => !c.musicUploaded);
  const readyToConfirm = drafts.filter((c) => c.musicUploaded);
  const rejected = ahead.filter((c) => c.status === 'rejected');
  const toInvoice = (pending || []).filter(isInvoiceable);
  const unpaid = (invoices || []).filter((i) => i.status === 'issued');
  const overdue = unpaid.filter((i) => (daysUntil(i.due_date) ?? 0) < 0);
  const openCompetitions = competitions.filter(
    (c) => c.status === 'active' && c.type !== 'grand_finale' && !isRegistrationClosed(c) && !isPastCompetition(c)
  );
  // Before the season: rounds open for pre-registration in which the group has nothing yet.
  const entered = new Set(choreographies.map((c) => c.competition?.id));
  const preRegistration = competitions
    .filter(
      (c) =>
        c.status === 'upcoming' &&
        c.type !== 'grand_finale' &&
        !isPastCompetition(c) &&
        (daysUntil(c.registrationStart) ?? 0) > 0 &&
        !entered.has(c.id)
    )
    .sort((a, b) => String(a.registrationStart).localeCompare(String(b.registrationStart)));

  if (dancers.length === 0) {
    todo.push({
      key: 'dancers',
      icon: Users,
      to: '/dancers',
      title: 'Přidejte tanečníky',
      text: 'Bez tanečníků nelze přihlásit choreografii. Můžete je nahrát i hromadně z Excelu.',
    });
  }
  if (canRegister.length > 0) {
    todo.push({
      key: 'register',
      icon: ListChecks,
      to: '/choreographies',
      urgent: true,
      title: `Zaregistrujte předregistrace (${canRegister.length})`,
      text: 'Registrace je otevřená. Předregistrované choreografie se do soutěže počítají až po registraci.',
    });
  }
  if (withoutMusic.length > 0) {
    todo.push(
      withDeadline(
        {
          key: 'music',
          icon: Music2,
          to: withoutMusic.length === 1 ? `/choreographies/${withoutMusic[0].id}` : '/choreographies',
          title: `Nahrajte hudbu – ${countLabel(withoutMusic.length, CHOREO_FORMS)}`,
          text: 'Hudba ve formátu MP3 je potřeba pro potvrzení prezence.',
        },
        'Nejbližší termín prezence',
        nearestDeadline(withoutMusic)
      )
    );
  }
  if (readyToConfirm.length > 0) {
    todo.push(
      withDeadline(
        {
          key: 'presence',
          icon: CheckCircle2,
          to: readyToConfirm.length === 1 ? `/choreographies/${readyToConfirm[0].id}` : '/choreographies',
          title: `Potvrďte prezenci – ${countLabel(readyToConfirm.length, CHOREO_FORMS)}`,
          text: 'Choreografie jsou rozpracované. Potvrzením prezence závazně potvrdíte účast.',
        },
        'Nejbližší termín prezence',
        nearestDeadline(readyToConfirm)
      )
    );
  }
  if (toInvoice.length > 0) {
    todo.push(
      withDeadline(
        {
          key: 'invoice',
          icon: Receipt,
          to: '/invoices',
          title: `Vystavte fakturu – ${countLabel(toInvoice.length, CHOREO_FORMS)} s potvrzenou prezencí`,
          text: 'Uhradit ji je potřeba nejpozději týden před soutěží.',
        },
        'Nejbližší termín úhrady',
        nearestDeadline(toInvoice)
      )
    );
  }
  if (rejected.length > 0) {
    todo.push({
      key: 'rejected',
      icon: AlertTriangle,
      to: '/choreographies',
      urgent: true,
      title: `Zamítnuté choreografie (${rejected.length})`,
      text: 'Kontaktujte prosím organizátory na info@bestofthebest.cz.',
    });
  }
  if (unpaid.length > 0) {
    todo.push({
      key: 'invoices',
      icon: CreditCard,
      to: '/invoices',
      urgent: overdue.length > 0,
      title: overdue.length > 0 ? `Faktury po splatnosti (${overdue.length})` : `Faktury k úhradě (${unpaid.length})`,
      text: 'Platební údaje včetně variabilního symbolu najdete ve Fakturaci.',
    });
  }
  if (dancers.length > 0 && ahead.length === 0 && openCompetitions.length > 0) {
    todo.push({
      key: 'enter',
      icon: Award,
      to: `/competitions/${openCompetitions[0].id}`,
      title: `Přihlaste choreografii na ${openCompetitions[0].name}`,
      text: 'Registrace je otevřená.',
    });
  }
  if (preRegistration.length > 0) {
    const first = preRegistration[0];
    todo.push({
      key: 'season',
      icon: CalendarDays,
      to: '/competitions',
      title: `Registrace na sezónu ${String(first.date).slice(0, 4)} se otevře ${formatDateLong(first.registrationStart)}`,
      text: 'Choreografie si můžete předregistrovat už teď – po otevření registrace je jedním kliknutím zaregistrujete.',
    });
  }
  return todo;
}

/** "Nejbližší termín prezence: 3. 4. 2027 (za 5 dní)", amber in the last week, red in the last two days. */
function DeadlineNote({ label, date }) {
  const days = daysUntil(date);
  const urgency = deadlineUrgency(date);
  const when = days !== null && days < 0 ? 'termín už uplynul' : countdownText(days);
  const tone =
    urgency === 'critical' ? 'font-medium text-red-700' : urgency === 'urgent' ? 'font-medium text-amber-800' : 'text-gray-600';
  return (
    <span className={`mt-0.5 block text-sm ${tone}`}>
      {label}: {formatDate(date)}
      {when && ` (${when})`}
    </span>
  );
}

/** The four steps from the organiser's website; collapsible (open for a group without entries). */
function RegistrationGuide({ open, onToggle }) {
  const linkClass = 'font-medium text-indigo-700 underline hover:text-indigo-900';
  return (
    <section className="rounded-lg bg-white shadow" aria-labelledby="guide-title">
      <h2 id="guide-title" className="text-lg font-semibold text-gray-900">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          aria-controls="registration-guide"
          className="flex w-full items-center justify-between gap-3 rounded-lg p-4 text-left hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 sm:px-6"
        >
          <span className="flex items-center gap-2">
            <HelpCircle className="h-5 w-5 text-indigo-600" aria-hidden="true" />
            Jak na registraci
          </span>
          <ChevronDown
            className={`h-5 w-5 flex-shrink-0 text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`}
            aria-hidden="true"
          />
        </button>
      </h2>
      {open && (
        <ol id="registration-guide" className="list-decimal space-y-2 pb-4 pl-10 pr-4 text-sm text-gray-700 sm:pl-12 sm:pr-6">
          <li>
            Přidejte tanečníky v sekci{' '}
            <Link to="/dancers" className={linkClass}>
              Tanečníci
            </Link>
            .
          </li>
          <li>
            V sekci{' '}
            <Link to="/competitions" className={linkClass}>
              Soutěže
            </Link>{' '}
            vyberte soutěž a kategorii a choreografii přihlaste (před otevřením registrace ji předregistrujte).
          </li>
          <li>Nahrajte hudbu (MP3) a nejpozději týden před soutěží potvrďte online prezenci.</li>
          <li>
            Vystavte si fakturu v sekci{' '}
            <Link to="/invoices" className={linkClass}>
              Fakturace
            </Link>{' '}
            a uhraďte ji nejpozději týden před soutěží.
          </li>
        </ol>
      )}
    </section>
  );
}

const Dashboard = () => {
  const { data, error, isLoading, reload } = useFetch(async () => {
    const invoicesPromise = api
      .get('/invoices/')
      .then((res) => res.data || [])
      .catch(() => null);
    // Choreographies waiting for an invoice - optional, the dashboard works without it.
    const pendingPromise = api
      .get('/invoices/pending-choreographies')
      .then((res) => (Array.isArray(res.data) ? res.data : []))
      .catch(() => null);
    const [dancersRes, competitionsRes, choreographiesRes, invoices, pending] = await Promise.all([
      dancersApi.getAll(),
      competitionsApi.getAll(),
      choreographiesApi.getAll(),
      invoicesPromise,
      pendingPromise,
    ]);
    return {
      dancers: dancersRes.data || [],
      competitions: competitionsRes.data || [],
      choreographies: choreographiesRes.data || [],
      invoices,
      pending,
    };
  }, 'all');

  const todo = useMemo(() => (data ? buildTodo(data) : []), [data]);
  const [guideOpen, setGuideOpen] = useState(null); // null = default (open for a group without entries)

  if (isLoading && !data) return <Spinner label="Načítám přehled…" />;
  if (error && !data) return <LoadError title="Přehled se nepodařilo načíst" error={error} onRetry={reload} />;

  // Only competitions that are still ahead (a finished round must not be listed as "upcoming");
  // after its registration deadline a round stays only when the group takes part in it.
  const entriesIn = (competitionId) => data.choreographies.filter((c) => c.competition?.id === competitionId).length;
  const upcoming = data.competitions
    .filter((c) => !['finished', 'archived'].includes(c.status) && (daysUntil(c.date) ?? 0) >= 0)
    .filter((c) => showInCompetitionLists(c, entriesIn(c.id)))
    .slice(0, 4);
  const showGuide = guideOpen ?? data.choreographies.length === 0;

  return (
    <div className="space-y-6">
      <PageHeader title="Přehled" subtitle="Co je potřeba udělat a nejbližší soutěže." />

      <section className="rounded-lg bg-white p-4 shadow sm:p-6" aria-labelledby="todo-title">
        <h2 id="todo-title" className="mb-3 text-lg font-semibold text-gray-900">
          Co je potřeba udělat
        </h2>
        {todo.length === 0 ? (
          <p className="flex items-center gap-2 text-sm text-gray-700">
            <CheckCircle2 className="h-5 w-5 text-green-600" aria-hidden="true" />
            Momentálně na nic nečekáme. Nové soutěže najdete v sekci Soutěže.
          </p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {todo.map((item) => {
              const Icon = item.icon;
              return (
                <li key={item.key}>
                  <Link
                    to={item.to}
                    className="flex items-start gap-3 py-3 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                  >
                    <Icon
                      className={`mt-0.5 h-5 w-5 flex-shrink-0 ${
                        item.critical ? 'text-red-600' : item.urgent ? 'text-amber-600' : 'text-indigo-600'
                      }`}
                      aria-hidden="true"
                    />
                    <span className="flex-1">
                      <span className="block font-medium text-gray-900">{item.title}</span>
                      <span className="block text-sm text-gray-600">{item.text}</span>
                      {item.deadline && <DeadlineNote label={item.deadline.label} date={item.deadline.date} />}
                    </span>
                    <ChevronRight className="mt-0.5 h-5 w-5 flex-shrink-0 text-gray-400" aria-hidden="true" />
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <RegistrationGuide open={showGuide} onToggle={() => setGuideOpen(!showGuide)} />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <section className="rounded-lg bg-white p-4 shadow sm:p-6 lg:col-span-2" aria-labelledby="competitions-title">
          <div className="mb-3 flex items-center justify-between gap-3">
            <h2 id="competitions-title" className="text-lg font-semibold text-gray-900">
              Nejbližší soutěže
            </h2>
            <Link to="/competitions" className="text-sm font-medium text-indigo-700 hover:text-indigo-900">
              Všechny soutěže
            </Link>
          </div>
          {upcoming.length === 0 ? (
            <p className="text-sm text-gray-600">Momentálně nejsou vypsané žádné soutěže.</p>
          ) : (
            <ul className="space-y-3">
              {upcoming.map((competition) => (
                <li key={competition.id}>
                  <Link
                    to={`/competitions/${competition.id}`}
                    className="block rounded-md border border-gray-200 p-3 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-gray-900">{competition.name}</span>
                      <CompetitionTypeBadge type={competition.type} />
                      <RegistrationBadge competition={competition} />
                    </div>
                    <div className="mt-1 flex flex-col gap-1 text-sm text-gray-600 sm:flex-row sm:flex-wrap sm:gap-x-4">
                      <span className="inline-flex items-center gap-1.5">
                        <CalendarDays className="h-4 w-4" aria-hidden="true" />
                        {formatDateLong(competition.date)}
                      </span>
                      {!isRegistrationClosed(competition) && <DeadlineText date={competition.registrationEnd} />}
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="space-y-4 rounded-lg bg-white p-4 shadow sm:p-6" aria-label="Skupina">
          <div className="grid grid-cols-2 gap-3">
            <Link to="/dancers" className="rounded-md border border-gray-200 p-3 hover:bg-gray-50">
              <span className="block text-2xl font-semibold text-gray-900">{data.dancers.length}</span>
              <span className="text-sm text-gray-600">Tanečníci</span>
            </Link>
            <Link to="/choreographies" className="rounded-md border border-gray-200 p-3 hover:bg-gray-50">
              <span className="block text-2xl font-semibold text-gray-900">{data.choreographies.length}</span>
              <span className="text-sm text-gray-600">Choreografie celkem</span>
            </Link>
          </div>
          <LogoUploader compact />
        </section>
      </div>
    </div>
  );
};

export default Dashboard;
